import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { ArrowRight, LockKey, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { resetPasswordSchema } from "@reactive-resume/dto";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import type { z } from "zod";
import { motion } from "framer-motion";

import { useResetPassword } from "@/client/services/auth";

type FormValues = z.infer<typeof resetPasswordSchema>;

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06, duration: 0.4, ease },
});

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [showPwd, setShowPwd] = useState(false);

  const { resetPassword, loading, error } = useResetPassword();

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, password: "" },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await resetPassword(data);
      void navigate("/client/auth/login");
    } catch {
      form.reset();
    }
  };

  useEffect(() => {
    if (!token) void navigate("/client/auth/forgot-password");
  }, [token, navigate]);

  return (
    <>
      <Helmet>
        <title>{t`Nouveau mot de passe`} — Mon CV Pro</title>
      </Helmet>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(0)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        MON CV PRO
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(1)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        Nouveau mot de passe
      </motion.h1>

      {/* Sous-titre */}
      <motion.p {...fadeUp(2)} className="mb-6 text-[0.84rem] leading-relaxed text-gray-400">
        Choisissez un mot de passe fort et sécurisé pour votre compte.
      </motion.p>

      <Form {...form}>
        <form
          ref={formRef}
          className="flex flex-col gap-y-3.5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <motion.div {...fadeUp(3)}>
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                    {t`Nouveau mot de passe`}
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <LockKey
                        size={16}
                        className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                      />

                      <Input
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t`Minimum 8 caractères`}
                        className="h-11 rounded-xl border border-[#C9D3E6] bg-white pl-10 pr-11 text-sm font-medium text-[#172033] caret-[#6687F0] shadow-sm transition-[border-color,box-shadow,background-color] placeholder:font-normal placeholder:text-[#98A2B3] hover:border-[#AEBDE0] focus-visible:border-[#6687F0] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#6687F0]/20"
                        {...field}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085] transition-colors hover:text-[#465BBD]"
                        tabIndex={-1}
                        aria-label={showPwd ? "Masquer" : "Afficher"}
                      >
                        {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </FormControl>

                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </motion.div>

          {error && (
            <motion.div {...fadeUp(3.5)}>
              <p className="rounded-xl border border-red-200 bg-red-50/80 px-3.5 py-2.5 text-[0.78rem] leading-relaxed text-red-600">
                {t`Impossible de réinitialiser le mot de passe. Ce lien est invalide ou a déjà été utilisé. Veuillez refaire une demande depuis la page « Mot de passe oublié ».`}
              </p>
            </motion.div>
          )}

          <motion.div {...fadeUp(4)} className="pt-0.5">
            <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
              <Button
                type="submit"
                disabled={loading}
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0]"
              >
                {t`Réinitialiser le mot de passe`}

                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Button>
            </motion.div>
          </motion.div>

          <motion.div {...fadeUp(5)} className="flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} className="shrink-0 text-[#3ecf8e]" />

            <span className="text-[0.72rem] text-gray-400">
              {t`Vos données sont chiffrées et sécurisées.`}
            </span>
          </motion.div>
        </form>
      </Form>
    </>
  );
};
